import {
    Car,
    Factory,
    Zap,
    CreditCard,
    Truck,
    Shield,
    Cog,
    BarChart3,
    Users,
    Workflow,
    FileCheck,
    RefreshCw,
    AlertTriangle,
    Activity,
    Landmark,
    Scale,
    Stethoscope,
    Building,
    Cloud,
    Cpu,
    Database,
    Globe,
    Server,
    Layers,
    Lock,
} from 'lucide-react';
import { BranchData } from '../types/branch';

export const branchesDe: Record<string, BranchData> = {
    automotive: {
        title: 'Automotive',
        subtitle: 'Kontrollierte Auslieferung und Governance für hochvernetzte Lieferketten mit hohen Anforderungen an Qualität und Traceability.',
        heroImage: '/images/automotive_hero.png',
        contextDesc: 'Automotive-Organisationen arbeiten in hochvernetzten Lieferketten mit hohen Anforderungen an Qualität, Verfügbarkeit und Nachweisbarkeit. Änderungen wirken oft über mehrere Systeme hinweg (ERP, Logistik, Analytics, Plattformen) und müssen kontrolliert ausgerollt werden.',
        operationalReality: [
            'Viele Schnittstellen (Lieferanten, Logistik, Qualitätsdaten), geringe Fehlertoleranz',
            'Hoher Druck auf Stabilität, Verfügbarkeit und Traceability',
            'Legacy + Modernisierung parallel, harte Release-Fenster',
        ],
        deliverables: [
            'Stabiler Delivery-Rhythmus und kontrollierte Releases über Systemgrenzen',
            'Sicherheits- und Governance-Baselines für Identitäten, Logs, Changes',
            'Integrationsdisziplin (Contracts, Monitoring, Fehlerpfade)',
        ],
        capabilities: [
            { icon: RefreshCw, title: 'Change Governance', desc: 'Release-/Rollback-Runbooks und Abhängigkeitsmanagement' },
            { icon: Cog, title: 'Schnittstellenverträge', desc: 'API/EDI-Contracts, Monitoring und Incident-Prozesse' },
            { icon: FileCheck, title: 'Evidenz & Compliance', desc: 'Dokumentierte Abnahmen, Change Logs und Audit Trails' },
            { icon: Car, title: 'Supply Chain Integration', desc: 'Lieferanten-, Logistik- und Qualitätsdaten-Anbindung' },
            { icon: BarChart3, title: 'Analytics', desc: 'Produktions- und Qualitätskennzahlen in Echtzeit' },
            { icon: Users, title: 'Ownership-Modell', desc: 'Service Owner, Integration Owner, Change Owner' },
        ],
        timeline: [
            {
                phase: 'Readiness & Inventory',
                weeks: '2–4 Wochen',
                items: ['System-Inventar', 'Integrations-Mapping', 'Risikobewertung', 'Governance-Plan'],
            },
            {
                phase: 'Controlled Execution',
                weeks: '6–12 Wochen',
                items: ['Release-Management', 'Integration Testing', 'Nachweise', 'Monitoring'],
            },
            {
                phase: 'Handover & Betrieb',
                weeks: '2–4 Wochen',
                items: ['Runbooks', 'Ownership-Übergabe', 'Checklisten', 'Lessons Learned'],
            },
        ],
        riskCompliance: [
            { category: 'Nachweisfähigkeit', items: ['Dokumentierte Abnahmen', 'Changes und Logs'] },
            { category: 'Rollback', items: ['Kriterien pro Release', 'Abhängigkeiten explizit geführt'] },
        ],
        enablement: [
            'Ownership-Modell: Service Owner, Integration Owner, Change Owner',
            'Runbooks + Übergabechecklisten',
        ],
        nextSteps: [
            { title: 'Orientierungsgespräch', duration: '30–45 Min.', desc: 'Erste Bestandsaufnahme Ihrer Situation' },
            { title: 'Assessment', duration: '2–4 Wochen', desc: 'System-/Integrationsinventar + Delivery-Plan' },
        ],
    },
    'energie-renewables': {
        title: 'Energie & Renewables',
        subtitle: 'Digitale Transformation für Energieversorger und erneuerbare Energien mit Fokus auf Datenqualität, IT/OT-Konvergenz und Compliance.',
        heroImage: '/images/energie_hero.png',
        contextDesc: 'Energieunternehmen stehen vor der Herausforderung, kritische Infrastruktur zu modernisieren, regulatorische Anforderungen zu erfüllen und gleichzeitig die Digitalisierung von Erzeugung, Verteilung und Verbrauch voranzutreiben.',
        operationalReality: [
            'SCADA/OT-Systeme mit anderen Lebenszyklen als IT',
            'KRITIS-Anforderungen und regulatorischer Druck',
            'Dezentrale Anlagen mit heterogenen Datenquellen',
        ],
        deliverables: [
            'IT/OT-Konvergenz mit klarer Schnittstellendefinition',
            'KRITIS-konforme Security Baseline',
            'Zentrale Datenplattform für Analytics und Reporting',
            'Predictive Maintenance für kritische Anlagen',
        ],
        capabilities: [
            { icon: Zap, title: 'Smart Grid', desc: 'Integration von Erzeugung, Verteilung und Speicherung' },
            { icon: Cpu, title: 'IoT & Sensorik', desc: 'Datenerfassung, Edge Computing und Real-time Analytics' },
            { icon: BarChart3, title: 'Predictive Maintenance', desc: 'Anomalieerkennung und vorausschauende Wartung' },
            { icon: Shield, title: 'IT/OT Security', desc: 'Absicherung von IT- und OT-Netzwerken' },
            { icon: FileCheck, title: 'Regulatorik', desc: 'KRITIS, EnWG und branchenspezifische Compliance' },
            { icon: Database, title: 'Data Platform', desc: 'Zentrale Datenhaltung für Analytics und Reporting' },
        ],
        timeline: [
            {
                phase: 'Assessment',
                weeks: '2–3 Wochen',
                items: ['Bestandsaufnahme', 'Technologie-Mapping', 'Gap-Analyse', 'Roadmap'],
            },
            {
                phase: 'Implementation',
                weeks: '8–16 Wochen',
                items: ['Integrationen', 'Security Baseline', 'Analytics Setup', 'Testing'],
            },
            {
                phase: 'Operations',
                weeks: '2–4 Wochen',
                items: ['Monitoring', 'Runbooks', 'Training', 'Handover'],
            },
        ],
        riskCompliance: [
            { category: 'KRITIS', items: ['BSI-Anforderungen', 'Meldepflichten', 'Prüfungen'] },
            { category: 'IT/OT Security', items: ['Netzwerksegmentierung', 'Access Control', 'Anomalieerkennung'] },
        ],
        enablement: [
            'Cross-funktionale Schulung für IT- und OT-Teams',
            'Runbooks für Incident Response in IT- und OT-Umgebungen',
        ],
        nextSteps: [
            { title: 'IT/OT Assessment', duration: '2–3 Wochen', desc: 'Analyse der Systemlandschaft und Sicherheitslage' },
            { title: 'Pilot', duration: '6–8 Wochen', desc: 'Proof of Concept für priorisierte Use Cases' },
        ],
    },
    'logistik-warehousing': {
        title: 'Logistik & Warehousing',
        subtitle: 'End-to-End-Transparenz für Ihre Supply Chain mit Fokus auf Automatisierung, Echtzeit-Tracking und Systemintegration.',
        heroImage: '/images/logistik_hero.png',
        contextDesc: 'Logistikunternehmen stehen unter Druck, Lieferketten transparenter, schneller und effizienter zu gestalten. Wir helfen bei der Integration von WMS, TMS und ERP sowie bei der Implementierung von Echtzeit-Tracking und Analytics.',
        operationalReality: [
            'Heterogene Systemlandschaft (WMS, TMS, ERP, Carrier-APIs)',
            'Hohe Anforderungen an Echtzeitdaten und SLAs',
            'Saisonale Peaks mit Skalierungsanforderungen',
        ],
        deliverables: [
            'End-to-End-Transparenz über die gesamte Lieferkette',
            'Nahtlose Integration von WMS, TMS und Carrier-Systemen',
            'Echtzeit-Tracking und Predictive Analytics',
            'Automatisierung von Routineprozessen',
        ],
        capabilities: [
            { icon: Truck, title: 'TMS Integration', desc: 'Transport Management System und Carrier-Anbindung' },
            { icon: Factory, title: 'WMS Optimization', desc: 'Warehouse Management und Bestandsführung' },
            { icon: Cog, title: 'EDI & API', desc: 'B2B-Integration und Echtzeit-Datenaustausch' },
            { icon: BarChart3, title: 'Demand Forecasting', desc: 'ML-basierte Prognosen und Kapazitätsplanung' },
            { icon: Cpu, title: 'IoT & Tracking', desc: 'Sensoren, RFID und GPS-Tracking' },
            { icon: Shield, title: 'Compliance', desc: 'Zoll, Gefahrgut und branchenspezifische Anforderungen' },
        ],
        timeline: [
            {
                phase: 'Discovery',
                weeks: '2–3 Wochen',
                items: ['Prozess-Mapping', 'Systemlandschaft', 'Pain Points', 'Quick Wins'],
            },
            {
                phase: 'Integration',
                weeks: '6–12 Wochen',
                items: ['API-Entwicklung', 'WMS/TMS-Anbindung', 'Testing', 'Rollout'],
            },
            {
                phase: 'Optimization',
                weeks: '2–4 Wochen',
                items: ['Analytics Setup', 'KPI-Dashboards', 'Training', 'Continuous Improvement'],
            },
        ],
        riskCompliance: [
            { category: 'Datenqualität', items: ['Master Data Management', 'Validierungsregeln'] },
            { category: 'Compliance', items: ['Zolldokumentation', 'Gefahrgut', 'Nachweispflichten'] },
        ],
        enablement: [
            'Training für Dispatcher und Warehouse-Teams',
            'Playbooks für Peak-Szenarien und Eskalationen',
        ],
        nextSteps: [
            { title: 'Supply Chain Assessment', duration: '1–2 Wochen', desc: 'Analyse der aktuellen Prozesse und Systeme' },
            { title: 'Integration Pilot', duration: '4–6 Wochen', desc: 'PoC für priorisierte Schnittstellen' },
        ],
    },
    'healthcare-medtech': {
        title: 'Healthcare & MedTech',
        subtitle: 'Sichere digitale Gesundheit. DiGA, KHZG und MDR-konforme Plattformen.',
        heroImage: '/images/healthcare_medtech_hero.png',
        contextDesc: 'Das Gesundheitswesen steht vor einem massiven Umbruch. KHZG-Förderungen, DiGA-Fast-Track und die elektronische Patientenakte (ePA) treiben die Digitalisierung. Gleichzeitig sind die Hürden durch DSGVO, MDR und Gematik-Vorgaben so hoch wie nie.',
        operationalReality: [
            'Veraltete KIS-Systeme und fehlende Interoperabilität (FHIR/HL7)',
            'Hohe regulatorische Hürden (MDR, DiGAV) bremsen Innovation',
            'Sensibelste Patientendaten erfordern höchstes Schutzniveau',
        ],
        deliverables: [
            'DiGA / DiPA Compliance Check & Fast-Track Begleitung',
            'FHIR-Interoperabilitäts-Layer für KIS-Integration',
            'Cloud-Plattformen für Gesundheitsdaten (C5, BSI-konform)',
            'MDR-konforme Software-Entwicklungsprozesse',
        ],
        capabilities: [
            { icon: Activity, title: 'Interoperabilität', desc: 'FHIR, HL7, Gematik-Anbindung und KIS-Integration' },
            { icon: Scale, title: 'Regulatory Affairs', desc: 'MDR, DiGAV, DSGVO im Gesundheitswesen' },
            { icon: Shield, title: 'Information Security', desc: 'ISO 27001, B3S Krankenhaus, Patientendatenschutz' },
            { icon: Cloud, title: 'Health Cloud', desc: 'Sichere Infrastruktur (HDS, C5) für Gesundheitsdaten' },
            { icon: Stethoscope, title: 'Digital Health', desc: 'Entwicklung von DiGA, DiPA und Patienten-Apps' },
            { icon: FileCheck, title: 'Quality Management', desc: 'QMS nach ISO 13485 für Software als Medizinprodukt' },
        ],
        timeline: [
            {
                phase: 'Regulatory Assessment',
                weeks: '2-4 Wochen',
                items: ['MDR-Klassifizierung', 'Datenschutz-Folgenabschätzung', 'Gap-Analyse'],
                validation: ['Regulatory Roadmap'],
            },
            {
                phase: 'Platform & Compliance',
                weeks: '3-6 Monate',
                items: ['Sichere Cloud-Architektur', 'QMS-Setup', 'Interoperabilitäts-Layer'],
                validation: ['Audit-Ready Status'],
            },
            {
                phase: 'Development & Certification',
                weeks: '4-8 Monate',
                items: ['MVP-Entwicklung', 'Technische Dokumentation', 'Penetration Tests'],
                validation: ['DiGA/MDR Zulassung'],
            },
        ],
        riskCompliance: [
            { category: 'Regulatory', items: ['MDR (Medical Device Regulation)', 'DiGAV', 'DSGVO', 'SGB V'] },
            { category: 'Technical', items: ['Gematik-Spezifikationen', 'FHIR-Standards', 'BSI TR'] },
        ],
        enablement: [
            'Schulung zu MDR-konformer Softwareentwicklung',
            'Datenschutz-Schulungen für medizinisches Personal',
            'Interoperabilitäts-Workshops (FHIR)',
        ],
        nextSteps: [
            { title: 'DiGA Check-up', duration: '2 Stunden', desc: 'Potenzialanalyse für digitale Gesundheitsanwendungen' },
            { title: 'Interoperabilitäts-Workshop', duration: '1 Tag', desc: 'Strategie für KIS-Integration und FHIR' },
        ],
    },
    'public-sector': {
        title: 'Public Sector & Verwaltung',
        subtitle: 'Digitale Souveränität für den modernen Staat. OZG-Umsetzung und sichere Infrastruktur.',
        heroImage: '/images/public_sector_hero.png',
        contextDesc: 'Die Verwaltung muss digital werden – sicher, barrierefrei und souverän. OZG 2.0 und die Registermodernisierung setzen enge Fristen. Wir bauen Brücken zwischen Legacy-Verfahren und modernen Cloud-Architekturen.',
        operationalReality: [
            'Heterogene IT-Landschaft und Fachverfahren',
            'Strenge Vorgaben zur Datensouveränität und BSI-Grundschutz',
            'Fachkräftemangel bremst die Umsetzung von OZG-Leistungen',
        ],
        deliverables: [
            'OZG-Umsetzung & "Einer für Alle"-Prinzip (EfA)',
            'Souveräne Cloud-Strategie (StackIT, Delos, Private Cloud)',
            'BSI-Grundschutz-Auditierung & Vorbereitung',
            'Barrierefreie Frontend-Entwicklung (BITV 2.0)',
        ],
        capabilities: [
            { icon: Landmark, title: 'Digital Sovereignty', desc: 'Strategien für digitale Unabhängigkeit und Open Source' },
            { icon: FileCheck, title: 'OZG & EfA', desc: 'Umsetzung von Online-Zugangsgesetz Leistungen' },
            { icon: Shield, title: 'IT-Grundschutz', desc: 'Sicherheitskonzepte nach BSI-Standards 200-x' },
            { icon: Users, title: 'Accessibility', desc: 'Barrierefreie IT nach BITV 2.0 und WCAG' },
            { icon: Building, title: 'Smart City', desc: 'Vernetzte Infrastruktur und IoT-Plattformen' },
            { icon: RefreshCw, title: 'Modernisierung', desc: 'Ablösung von Fachverfahren und Legacy-Systemen' },
        ],
        timeline: [
            {
                phase: 'Bedarfsanalyse & Konzeption',
                weeks: '4-8 Wochen',
                items: ['Anforderungskatalog', 'Sicherheitskonzept', 'Lösungsarchitektur'],
                validation: ['Projektauftrag'],
            },
            {
                phase: 'Umsetzung & Pilotierung',
                weeks: '6-12 Monate',
                items: ['MVP-Entwicklung', 'BSI-Audit Vorbereitung', 'Pilotbetrieb'],
                validation: ['Go-Live Pilot'],
            },
            {
                phase: 'Rollout & Betrieb',
                weeks: '3-6 Monate',
                items: ['Flächenrollout', 'Betriebshandbuch', 'Schulungen'],
                validation: ['Abnahme'],
            },
        ],
        riskCompliance: [
            { category: 'Regulatory', items: ['OZG', 'BSI IT-Grundschutz', 'BITV 2.0', 'DSGVO'] },
            { category: 'Sovereignty', items: ['Open Source', 'Vermeidung von Vendor Lock-in', 'Datenhaltung DE'] },
        ],
        enablement: [
            'Schulungen zu BSI-Grundschutz',
            'Workshops für barrierefreie Entwicklung',
            'Agile Methoden in der Verwaltung',
        ],
        nextSteps: [
            { title: 'OZG-Umsetzungs-Check', duration: '1 Tag', desc: 'Statusanalyse und Roadmap' },
            { title: 'Souveränitäts-Workshop', duration: '4 Stunden', desc: 'Cloud-Strategie für die Verwaltung' },
        ],
    },
    fintech: {
        title: 'Fintech',
        subtitle: 'Sichere, skalierbare Technologielösungen für Finanzdienstleister mit Fokus auf Compliance, Prüfbarkeit und schnelle Releases.',
        heroImage: '/images/fintech_hero.png',
        contextDesc: 'Fintech-Organisationen müssen Vertrauen, Verfügbarkeit und Nachweisbarkeit gleichzeitig liefern. Anforderungen an Auditability, Zugriffskontrolle, Incident Readiness und Vendor Risk sind operative Pflicht – nicht optional.',
        operationalReality: [
            'Hoher Druck auf sichere Releases und schnelle Reaktionsfähigkeit',
            'Strenge Nachweise über Zugriffe, Änderungen, Freigaben und Incidents',
            'Abhängigkeit von Drittanbietern (Payment, KYC, Cloud), komplexe Datenflüsse',
        ],
        deliverables: [
            'Governance- und Nachweisstruktur für Changes, Releases und Controls',
            'Security/Privacy Baseline inkl. Incident-Playbooks und Übungen',
            'Delivery-Disziplin: CI-Gates, Teststrategie, Rollback-Pfade, Observability',
        ],
        capabilities: [
            { icon: Lock, title: 'Identity & Access', desc: 'Least Privilege, MFA, Admin-Audit' },
            { icon: BarChart3, title: 'Observability', desc: 'Zentrale Logs, Alerts, SLOs' },
            { icon: Workflow, title: 'Release Controls', desc: 'Quality Gates, Freigaben, Change Logs' },
            { icon: Globe, title: 'Vendor Controls', desc: 'Zugriffsmatrix, Evidenzanforderungen, regelmäßige Reviews' },
            { icon: CreditCard, title: 'Payment Security', desc: 'PCI-DSS, Tokenization, Fraud Detection' },
            { icon: AlertTriangle, title: 'Incident Response', desc: 'Playbooks, Eskalationspfade, Tabletop-Exercises' },
        ],
        timeline: [
            {
                phase: 'Baseline & Evidence Assessment',
                weeks: '2–3 Wochen',
                items: ['Kontrollen-Inventar', 'Gap-Analyse', 'Risikobewertung', 'Priorisierung'],
            },
            {
                phase: 'Controls & Guardrails',
                weeks: '4–8 Wochen',
                items: ['Security Controls', 'CI/CD Gates', 'Monitoring', 'Documentation'],
            },
            {
                phase: 'Operationalization',
                weeks: '4–8 Wochen',
                items: ['Training', 'Tabletop-Exercise', 'Continuous Monitoring', 'Review-Cadence'],
            },
        ],
        riskCompliance: [
            { category: 'Nachweisfähigkeit', items: ['Freigaben', 'Logs', 'Releases', 'Trainings'] },
            { category: 'Incident Readiness', items: ['Eskalationspfade', 'Runbooks', 'Übungsformate'] },
        ],
        enablement: [
            'Ownership-Modell: Security/Privacy Owner, Service Owner, Change Owner',
            'Runbooks + Checklisten für Betrieb und Changes',
        ],
        nextSteps: [
            { title: 'Evidence/Control Baseline Call', duration: '60–90 Min.', desc: 'Erste Bestandsaufnahme Ihrer Kontrollen' },
            { title: 'Assessment', duration: '2–3 Wochen', desc: 'Kontrollen + Nachweise + Umsetzungsplan' },
        ],
    },
    'fertigung-iiot': {
        title: 'Fertigung & Industrial IoT',
        subtitle: 'Digitalisierung der Produktion mit OT/IT-Integration, Predictive Maintenance und datengetriebener Optimierung.',
        heroImage: '/images/fertigung_hero.png',
        contextDesc: 'Fertigungsunternehmen stehen vor der Herausforderung, Produktionsdaten zu nutzen, ohne die Stabilität und Sicherheit der OT-Umgebung zu gefährden. Wir helfen bei der sicheren IT/OT-Integration und beim Aufbau von Analytics-Plattformen.',
        operationalReality: [
            'OT-Systeme mit langen Lebenszyklen und eingeschränkten Update-Möglichkeiten',
            'Siloartige Daten in Maschinen, SCADA und MES',
            'Fokus auf Verfügbarkeit und Produktionssicherheit',
        ],
        deliverables: [
            'Sichere IT/OT-Integration ohne Produktionsrisiko',
            'Zentrale Datenplattform für Produktionsanalytics',
            'Predictive Maintenance für kritische Anlagen',
            'OEE-Optimierung durch datengetriebene Insights',
        ],
        capabilities: [
            { icon: Cpu, title: 'Sensor Integration', desc: 'Anbindung von SPS, SCADA und Sensoren' },
            { icon: Server, title: 'Edge Computing', desc: 'Lokale Datenverarbeitung und Latenzminimierung' },
            { icon: Database, title: 'Data Lake', desc: 'Zentrale Datenspeicherung für Analytics' },
            { icon: BarChart3, title: 'Manufacturing Analytics', desc: 'OEE, Qualitätskennzahlen und Trendanalysen' },
            { icon: Shield, title: 'OT Security', desc: 'Absicherung von Produktionsnetzwerken' },
            { icon: Layers, title: 'MES Integration', desc: 'Manufacturing Execution System Anbindung' },
        ],
        timeline: [
            {
                phase: 'Assessment',
                weeks: '2–3 Wochen',
                items: ['Anlagen-Inventar', 'Datenquellen-Mapping', 'Security Review', 'Use Case Priorisierung'],
            },
            {
                phase: 'Pilot',
                weeks: '6–10 Wochen',
                items: ['Sensor-Integration', 'Edge Setup', 'Analytics MVP', 'Validierung'],
            },
            {
                phase: 'Scale',
                weeks: '4–12 Wochen',
                items: ['Rollout', 'Training', 'Monitoring', 'Continuous Improvement'],
            },
        ],
        riskCompliance: [
            { category: 'OT Security', items: ['Netzwerksegmentierung', 'Anomalieerkennung', 'Access Control'] },
            { category: 'Verfügbarkeit', items: ['Failsafe-Mechanismen', 'Rollback-Pläne', 'Testing in Nicht-Produktionszeiten'] },
        ],
        enablement: [
            'Cross-funktionale Schulung für IT- und OT-Teams',
            'Playbooks für Incident Response und Maintenance',
        ],
        nextSteps: [
            { title: 'IIoT Readiness Assessment', duration: '2–3 Wochen', desc: 'Analyse der Produktionslandschaft und Use Cases' },
            { title: 'Pilot', duration: '6–8 Wochen', desc: 'PoC für priorisierte Anlagen' },
        ],
    },
};
